/**
 * Dietary iron (4.39, N1) — the validity rule and the ONE display formatter.
 * Pure, no imports: mobile reaches it via `@/shared/iron`, the webapp by relative
 * path (like `shared/stoneOrder.ts`).
 *
 * Iron is tracking only (no default target, never scored). Unknown is not zero:
 * a value is KNOWN only when it is a JSON number, finite and ≥ 0. Anything else —
 * absent, null, NaN, a negative, or a string ("5", "5 mg") — is unknown and is
 * never coerced to 0, summed or shown as 0. Callers show "—" plus the coverage
 * copy for unknown and never pass it to {@link formatIronMg}.
 *
 * Every writer stores iron at 2 decimals: the generic whole-number rounding
 * would turn a 0.04 mg food into a measured 0.
 */

/** Stored precision for `ironMg` (mg). */
export const IRON_DECIMALS = 2;

/**
 * 4.40 RA: the server's defensive per-food / per-serving iron ceiling
 * (functions/src/utils/iron.ts IRON_MAX_MG). Exported for the recipe
 * known-ingredient builder only (utils/recipeKnown.ts), which must drop the same
 * over-ceiling iron the server's validator would reject; a test pins the two equal.
 */
export const IRON_MAX_MG = 200;

/** The validity rule: a finite, non-negative JSON number. */
export function isKnownIronMg(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0;
}

/** Round to the stored precision (2 decimals). */
export function roundIronMg(v: number): number {
  return Math.round(v * 100) / 100;
}

/**
 * Parse a typed amount, accepting a decimal comma ("0,04"). Returns undefined for
 * blank, non-numeric, non-finite or negative text — the key is then removed, never
 * stored as 0.
 */
export function parseIronInput(text: string | null | undefined): number | undefined {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return undefined;
  const v = parseFloat(trimmed.replace(/,/g, '.'));
  if (!Number.isFinite(v) || v < 0) return undefined;
  return roundIronMg(v);
}

/**
 * Display a KNOWN iron amount in mg (no unit):
 *   0              → "0"
 *   0 < v < 0.05   → "<0.1"
 *   under 10       → one decimal, whole values bare ("3", not "3.0")
 *   10 and over    → whole number
 * Localised like `formatNutrientAmount` (utils/nutrients.ts). Returns "—" for a
 * value that is not known (callers should not pass one).
 */
export function formatIronMg(v: number): string {
  if (!isKnownIronMg(v)) return '—';
  if (v === 0) return (0).toLocaleString();
  if (v < 0.05) return `<${(0.1).toLocaleString()}`;
  const oneDecimal = Math.round(v * 10) / 10;
  return (oneDecimal >= 10 ? Math.round(oneDecimal) : oneDecimal).toLocaleString();
}

/** Editor seed text: 2 decimals, so a stored 0.04 seeds "0.04", not "0". '' when unknown. */
export function ironInputText(v: unknown): string {
  return isKnownIronMg(v) ? String(roundIronMg(v)) : '';
}
